import { Directive, Input, OnChanges, OnDestroy, OnInit, signal, SimpleChanges, ViewChild, WritableSignal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  DsButtonIntent,
  DsButtonVariants,
  DsModalRef,
  DsModalService,
  DsModalVariant,
  DsSearchBarComponent,
  DsTooltipPosition,
  Icons,
} from '@levelaccess/design-system';
import { SecurityGroupService } from '@client/app/services/security-group.service';
import { NotificationPosition } from '@client/app/models/notification.model';
import { NotificationService } from '@client/app/services/notification.service';
import { RemoveUserModalComponent } from '@client/app/components/abstract-users-list/remove-user-modal/remove-user-modal.component';
import {
  AddUserModalComponent,
  IAddUserModalResult,
  IRoleOption,
} from '@client/app/components/abstract-users-list/add-user-modal/add-user-modal.component';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import {
  $securityEntity,
  $securityGroup,
  $securityGroups,
  $securityGroupUsers,
  SecurityEntityLevel,
} from '@shared/constants/security-group';
import { $user } from '@shared/constants/user';
import {
  ISecurityEntity,
  ISecurityGroup,
  ISecurityGroupsResponse,
  ISecurityGroupWithUserIds,
  ISecurityUserWithGroups,
  ISecurityUserWithGroupsResponse,
} from '@shared/interfaces/security-group.interface';
import { FileExtension } from '@shared/constants/file-extension';
import { RequiredSecurities } from '@shared/constants/required-securities';
import { AsyncReportService } from '../../services/async-report.service';
import { ITableColumn, ITableConfig, ITableRow } from '../table/ngb-table/utilities/ngb-table.interface';
import { NgbTableUtilities } from '../table/ngb-table/utilities/ngb-table.utilities';
import { LoadErrorHandler } from '../../services/load-error-handler';
import { SecurityUserService } from '../../services/security-user.service';
import { UserService } from '../../services/user.service';
import { DEBOUNCE_TIME } from '../../shared/constants';
import { TranslateService } from '../../translate/translate.service';
import { AngularUtility } from '../../utility/angular.utility';
import { UserAclService } from '../../services/user-acl.service';
import { AclSecurityAdapter } from '../../services/acl.service';

enum TableColumn {
  NAME = 'name',
  EMAIL = 'email',
  GROUPS = 'groups',
  ACTIONS = 'actions',
}

@Directive()
export abstract class AbstractUsersListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() tenantId?: string;
  @Input() workspaceId?: string;
  @Input() digitalPropertyId?: string;
  @ViewChild(DsSearchBarComponent) searchBarComponent?: DsSearchBarComponent;

  protected entityLevel: SecurityEntityLevel;
  protected errorTranslationKey: string;
  protected emptyStateTranslationKey: string;
  protected captionTranslationKey: string;

  protected readonly PAGE_SIZE: number = 10;

  tableConfig: ITableConfig;
  tableRows$: Observable<ITableRow<ISecurityUserWithGroups>[]>;
  totalRows$: Observable<number>;
  page$: BehaviorSubject<number> = new BehaviorSubject<number>(1);
  protected usersResponse$: Observable<ISecurityUserWithGroupsResponse>;
  protected showExportButton$: Observable<boolean>;

  protected readonly searchTerm$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  protected readonly groupFilter$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  protected readonly subscription: Subscription = new Subscription();

  protected readonly availableGroups: WritableSignal<IRoleOption[]> = signal([]);

  protected readonly FileExtension: typeof FileExtension = FileExtension;

  protected constructor(
    protected readonly userService: UserService,
    protected readonly securityUserService: SecurityUserService,
    protected readonly loadErrorHandler: LoadErrorHandler,
    protected readonly translateService: TranslateService,
    protected readonly securityGroupService: SecurityGroupService,
    protected readonly notificationService: NotificationService,
    protected readonly dsModalService: DsModalService,
    protected readonly userAclService: UserAclService,
    protected readonly asyncReportService: AsyncReportService,
  ) {}

  ngOnInit(): void {
    this.tableConfig = this.buildTableConfig();
    this.loadGroup();
    this.initUsersData();
    this.loadExportPermissions();
  }

  resetFilters(): void {
    this.page$.next(1);
    this.searchTerm$.next('');
    this.searchBarComponent?.clear();
    this.groupFilter$.next(null);
    this.loadGroup();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const tenantIdChanged = changes['tenantId'] && !changes['tenantId'].firstChange;
    const workspaceIdChanged = changes['workspaceId'] && !changes['workspaceId'].firstChange;
    const digitalPropertyIdChanged = changes['digitalPropertyId'] && !changes['digitalPropertyId'].firstChange;

    if (tenantIdChanged || workspaceIdChanged || digitalPropertyIdChanged) {
      this.resetFilters();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  handleSearch(searchTerm: string): void {
    this.searchTerm$.next(searchTerm);
    this.page$.next(1);
  }

  onPageChange(page: number): void {
    this.page$.next(page);
  }

  protected getUsersResponse$(): Observable<ISecurityUserWithGroupsResponse> {
    return this.page$.pipe(
      switchMap((page: number) =>
        this.searchTerm$.pipe(
          debounceTime(DEBOUNCE_TIME),
          distinctUntilChanged(),
          switchMap((searchTerm: string) =>
            this.groupFilter$.pipe(
              switchMap((securityGroupId: string | null) =>
                this.securityUserService
                  .findUsersWithSecurityGroupsByTenantId({
                    searchTerm,
                    securityGroupId,
                    tenantId: this.tenantId,
                    workspaceId: this.workspaceId,
                    digitalPropertyId: this.digitalPropertyId,
                    entityLevel: this.entityLevel,
                    isStaffGroup: false,
                    isServiceGroup: false,
                    limit: this.PAGE_SIZE,
                    page,
                  })
                  .pipe(
                    catchError((error: HttpErrorResponse) => {
                      this.loadErrorHandler.handleError(this.errorTranslationKey, error, this.constructor.name);

                      return of<ISecurityUserWithGroupsResponse>({
                        [$securityGroupUsers.users]: [],
                        [$securityGroupUsers._total]: 0,
                      });
                    }),
                    switchMap((response: ISecurityUserWithGroupsResponse) => this.handlePageOverflow(response, page)),
                  ),
              ),
            ),
          ),
        ),
      ),
      AngularUtility.shareRef<ISecurityUserWithGroupsResponse>(),
    );
  }

  protected getTableRows$(): Observable<ITableRow<ISecurityUserWithGroups>[]> {
    return this.getUsersForTableRows$().pipe(
      map((users: ISecurityUserWithGroups[]) => {
        const currentUserId: string = this.userService.getStoredUserId();
        const currentUserIndex: number = users.findIndex((user: ISecurityUserWithGroups) => user[$user._id] === currentUserId);

        const orderedUsers: ISecurityUserWithGroups[] =
          currentUserIndex !== -1
            ? [users[currentUserIndex], ...users.slice(0, currentUserIndex), ...users.slice(currentUserIndex + 1)]
            : users;

        return orderedUsers.map((user: ISecurityUserWithGroups) => this.buildTableRow(user));
      }),
    );
  }

  protected getTotalRows$(): Observable<number> {
    return this.usersResponse$.pipe(map((response: ISecurityUserWithGroupsResponse) => response[$securityGroupUsers._total]));
  }

  protected getUsersForTableRows$(): Observable<ISecurityUserWithGroups[]> {
    return this.usersResponse$.pipe(map((response: ISecurityUserWithGroupsResponse) => response[$securityGroupUsers.users]));
  }

  protected onRemoveUser(user: ISecurityUserWithGroups): void {
    this.subscription.add(
      this.securityGroupService.findAllSecurityGroupsByUserIdAndTenantId(user[$user._id], { tenantId: this.tenantId }).subscribe({
        next: (response: ISecurityGroupsResponse) => {
          const userGroupIds: string[] = user[$securityGroups.groups].map((group: ISecurityGroup) => group[$securityGroup._id]);
          const retainedGroupIds: string[] = response[$securityGroups.groups]
            .map((group: ISecurityGroup) => group[$securityGroup._id])
            .filter((groupId: string) => !userGroupIds.includes(groupId));

          this.removeUser(user, retainedGroupIds);
        },
        error: (error: HttpErrorResponse) => {
          this.loadErrorHandler.handleError('user_removed_error', error, this.constructor.name);
        },
      }),
    );
  }

  protected onAddUsers(): void {
    const modalRef: DsModalRef = this.dsModalService.open(AddUserModalComponent, DsModalVariant.center);

    modalRef.componentInstance.availableRoles = this.availableGroups();
    modalRef.componentInstance.tenantId = this.tenantId;
    modalRef.componentInstance.excludeGroupIds = this.availableGroups().map((r: IRoleOption) => r.id);
    modalRef.result
      .then((result: IAddUserModalResult) => {
        const userIds: string[] = result.users.map((user: ISecurityUserWithGroups) => user[$user._id]);

        this.subscription.add(
          this.securityUserService
            .bulkUpdateUsersSecurityGroups({ userIds, groupIds: [result.groupId], tenantId: this.tenantId })
            .subscribe({
              next: () => {
                this.notifyUsersAdded(userIds, result.groupId);
                this.reloadUsersData();
              },
              error: (error: HttpErrorResponse) => {
                this.loadErrorHandler.handleError('user_added_error', error, this.constructor.name);
              },
            }),
        );
      })
      .catch(() => {});
  }

  onDownloadReport(fileExtension: FileExtension): void {
    this.asyncReportService.init({
      getReport$: this.securityUserService.downloadReport(fileExtension, this.workspaceId, this.digitalPropertyId),
      postReport$: this.securityUserService.generateReport(
        fileExtension,
        this.workspaceId,
        this.digitalPropertyId,
        this.entityLevel,
      ),
      messages: {
        reportIsBeingGenerated: this.translateService.instant('report_is_being_generated'),
        reportHasBeenGenerated: (): Observable<string> =>
          of(this.translateService.instant('user_list_report_has_been_generated')),
        reportIsDownloading: (): Observable<string> => of(this.translateService.instant('file_is_downloading')),
        reportGenerationError: of(this.translateService.instant('user_list_report_generation_error')),
        reportDownloadError: of(this.translateService.instant('user_list_report_download_error')),
      },
    });

    this.asyncReportService.downloadReport();
  }

  private removeUser(user: ISecurityUserWithGroups, retainedGroupIds: string[]): void {
    const modalRef: DsModalRef = this.dsModalService.open(RemoveUserModalComponent, DsModalVariant.center);
    modalRef.componentInstance.willBeRemoved = retainedGroupIds.length === 0;
    modalRef.componentInstance.groupName = user[$securityGroups.groups]
      .map((group: ISecurityGroup) => group[$securityGroup.name])
      .join(', ');

    modalRef.result
      .then(() => {
        this.subscription.add(
          this.securityUserService.updateUserSecurityGroups(user[$user._id], retainedGroupIds, this.tenantId).subscribe({
            next: () => {
              this.notificationService.success(this.translateService.instant('user_removed'), NotificationPosition.Toast, true);
              this.reloadUsersData();
            },
            error: (error: HttpErrorResponse) => {
              this.loadErrorHandler.handleError('user_removed_error', error, this.constructor.name);
            },
          }),
        );
      })
      .catch(() => {});
  }

  private buildTableConfig(): ITableConfig {
    const columns: Record<TableColumn, ITableColumn> = {
      [TableColumn.NAME]: {
        translationKey: 'name',
      },
      [TableColumn.EMAIL]: {
        translationKey: 'email',
      },
      [TableColumn.GROUPS]: {
        translationKey: 'groups_label',
      },
      [TableColumn.ACTIONS]: {
        translationKey: 'column_header_action',
      },
    };

    return {
      columns,
      emptyState: {
        subtitle: this.translateService.instant(this.emptyStateTranslationKey),
      },
      caption: this.translateService.instant(this.captionTranslationKey),
    };
  }

  private buildTableRow(user: ISecurityUserWithGroups): ITableRow<ISecurityUserWithGroups> {
    const isCurrentUser: boolean = user[$user._id] === this.userService.getStoredUserId();

    return {
      key: user[$user._id],
      originalData: user,
      data: {
        [TableColumn.NAME]: NgbTableUtilities.textCell({
          text: user[$user.displayName],
        }),
        [TableColumn.EMAIL]: NgbTableUtilities.textCell({
          text: user[$user.email],
        }),
        [TableColumn.GROUPS]: NgbTableUtilities.DSTooltipItemsCell({
          items: user[$securityGroups.groups].map((group: ISecurityGroup) => group[$securityGroup.name]),
          itemDisplaySeparator: '\n',
          maxVisibleItems: 2,
          additionalItemsTranslationKey: 'additional_security_groups',
          tooltipPlacement: DsTooltipPosition.bottom,
          styles: { whiteSpace: 'pre-wrap' },
        }),
        [TableColumn.ACTIONS]: isCurrentUser
          ? NgbTableUtilities.emptyCell()
          : NgbTableUtilities.DSbuttonCell({
              text: this.translateService.instant('action_remove'),
              variant: DsButtonVariants.microAction,
              intent: DsButtonIntent.destructive,
              microActionIcon: Icons.CircleXmark,
              clickListener: () => this.onRemoveUser(user),
            }),
      },
    };
  }

  private handlePageOverflow(
    response: ISecurityUserWithGroupsResponse,
    page: number,
  ): Observable<ISecurityUserWithGroupsResponse> {
    const total: number = response[$securityGroupUsers._total];
    const lastPage: number = Math.ceil(total / this.PAGE_SIZE) || 1;

    if (response[$securityGroupUsers.users].length === 0 && total > 0 && page > lastPage) {
      this.page$.next(lastPage);

      return EMPTY;
    }

    return of(response);
  }

  private initUsersData(): void {
    this.usersResponse$ = this.getUsersResponse$();
    this.tableRows$ = this.getTableRows$();
    this.totalRows$ = this.getTotalRows$();
  }

  private reloadUsersData(): void {
    this.page$.next(this.page$.value);
  }

  private loadGroup(): void {
    this.subscription.add(
      this.securityGroupService
        .findSecurityGroupsForUserManagement({
          tenantId: this.tenantId,
          entityLevel: this.entityLevel,
          includeStaffGroups: false,
        })
        .pipe(
          map((response: ISecurityGroupsResponse<ISecurityGroupWithUserIds>) => {
            return response[$securityGroups.groups].filter((group: ISecurityGroupWithUserIds) => {
              const entities: ISecurityEntity[] = group[$securityGroup.entities];

              if (this.digitalPropertyId) {
                return entities.some(
                  (entity: ISecurityEntity) => entity[$securityEntity.digitalPropertyId] === this.digitalPropertyId,
                );
              }

              if (this.workspaceId) {
                return entities.some((entity: ISecurityEntity) => entity[$securityEntity.workspaceId] === this.workspaceId);
              }

              return true;
            });
          }),
          map((filteredGroups: ISecurityGroupWithUserIds[]) => {
            const roleOptions: IRoleOption[] = filteredGroups.map((group: ISecurityGroupWithUserIds) => ({
              id: group[$securityGroup._id],
              name: group[$securityGroup.name],
            }));
            this.availableGroups.set(roleOptions);
          }),
        )
        .subscribe({
          error: (error: HttpErrorResponse) => {
            this.loadErrorHandler.handleError('organization_details_users_loading_error', error, this.constructor.name);
          },
        }),
    );
  }

  private notifyUsersAdded(userIds: string[], groupId: string): void {
    const roleName: string = this.availableGroups().find((g: IRoleOption) => g.id === groupId)?.name ?? '';
    const message: string =
      userIds.length === 1
        ? this.translateService.instant('user_added_with_role', [roleName.toLowerCase()])
        : this.translateService.instant('users_added_with_role', [userIds.length, roleName.toLowerCase()]);
    this.notificationService.success(message, NotificationPosition.Toast, true);
  }

  private loadExportPermissions(): void {
    const canExportUsers$: Observable<boolean> = this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter
            .useTenantFromUser()
            .useFunctionalActions(RequiredSecurities.UM_User_Administration_Download.functionalActions)
            .check(),
        ),
      );

    this.showExportButton$ = combineLatest([this.totalRows$, canExportUsers$]).pipe(
      map(([totalRows, canExportUsers]: [number, boolean]) => {
        return totalRows > 0 && canExportUsers;
      }),
    );
  }
}
